import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useData } from '../data.js';
import { useRecords, PageHead, Empty, Err } from '../ui.js';
import { CreateMenu } from './CreateMenu.js';
import { revealContactById, ApiError } from '../api.js';
import type { Contact } from '../api.js';

// Contacts as a first-class object.
//
// They existed in the database from day one but had no view — the only way to
// see a person was to open their account and read the sidebar. That makes the
// obvious questions unanswerable: who do I have an address for, who is
// suppressed, who has nobody working them.
//
// ─────────────────────────────────────────────────────────────────────────────
// AND THEN A WAY TO ACT ON THE ANSWER (2026-08-26).
//
// The screen could tell you a person had no address and offered nothing to do
// about it. "not revealed" was a dead end: a flag naming a gap, on a list, with
// no control beside it. Kyle's note — "we're missing a way to select and grab
// things for enrichment" — is exactly that.
//
// WHY THIS IS NOT THE BULK REVEAL build-core REFUSES TO BUILD. That refusal is
// about spending on people nobody looked at: a walk over twenty-five accounts
// that quietly buys every address it finds. This is the opposite. Every person
// here is one Kyle ticked, the bar counts them and prices them before anything
// happens, and the confirm names the number out loud. Selection IS the looking.
//
// IT GOES THROUGH THE SINGLE-REVEAL ROUTE, ONE PERSON AT A TIME. No batch
// endpoint was added, so there is still exactly one code path that spends an
// Apollo credit and exactly one place it is audited. A run that fails halfway
// has bought what it bought and says so per person, rather than rolling back a
// spend that has already happened at the provider.

type Row = Contact & { id: string; company_id: string; lifecycle: string; title: string | null };

export function Contacts() {
  const { companies } = useData();
  const { rows, reload } = useRecords<Row>('contacts');
  const [q, setQ] = useState('');
  // Today's "Get an address" batch deep-links to ?filter=no_email — the
  // screen opens already showing the people the click was about.
  const [params] = useSearchParams();
  const fromUrl = params.get('filter');
  const [filter, setFilter] = useState<'all' | 'emailed' | 'no_email' | 'suppressed'>(
    fromUrl === 'no_email' || fromUrl === 'emailed' || fromUrl === 'suppressed'
      ? fromUrl : 'all');
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [result, setResult] = useState<{ found: number; missing: number; failed: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const byCompany = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companies ?? []) m.set(c.id, c.name);
    return m;
  }, [companies]);

  const slugOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companies ?? []) m.set(c.id, c.slug);
    return m;
  }, [companies]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows ?? []).filter((c) => {
      if (filter === 'emailed' && !c.email) return false;
      if (filter === 'no_email' && c.email) return false;
      if (filter === 'suppressed' && !c.suppressed) return false;
      if (filter !== 'suppressed' && c.suppressed) return false;
      if (!needle) return true;
      return (
        c.name.toLowerCase().includes(needle) ||
        (c.title ?? '').toLowerCase().includes(needle) ||
        (c.email ?? '').toLowerCase().includes(needle) ||
        (byCompany.get(c.company_id) ?? '').toLowerCase().includes(needle)
      );
    });
  }, [rows, q, filter, byCompany]);

  // Only the ones a credit would actually buy something for. Someone already
  // revealed costs a credit and returns what we have; someone suppressed must
  // never be worked at all.
  const needAddress = useMemo(
    () => filtered.filter((c) => picked.has(c.id) && !c.email && !c.suppressed),
    [filtered, picked],
  );

  async function reveal() {
    setConfirming(false);
    setError(null);
    setResult(null);
    let found = 0, missing = 0;
    const failed: string[] = [];

    // Serial, not parallel. Each one is a credit, and a burst of parallel
    // reveals makes a partial failure impossible to reason about afterwards.
    for (const c of needAddress) {
      const slug = slugOf.get(c.company_id);
      if (!slug) { failed.push(`${c.name}: no account`); continue; }
      setRunning(c.id);
      try {
        const r = await revealContactById(slug, c.id);
        // Apollo charges whether or not it finds an address, so "no address"
        // is a real outcome that cost a credit — not an error to hide.
        if (r.email_found) found++; else missing++;
      } catch (e) {
        failed.push(`${c.name}: ${e instanceof ApiError ? e.message : String(e)}`);
      }
    }
    setRunning(null);
    setPicked(new Set());
    setResult({ found, missing, failed });
    // The run just SPENT MONEY — the list must show what it bought. Without
    // this the freshly-revealed addresses kept rendering "not revealed" and
    // invited a second spend on the same people.
    void reload();
  }

  if (!rows) return <p className="loading">Loading…</p>;

  return (
    <>
      <PageHead
        title="Contacts" count={filtered.length}
        sub="Everyone on a target committee, and whether you can actually reach them."
        action={<button className="primary" onClick={() => setCreating(true)}>New contact</button>}
      />

      <div className="toolbar">
        <input className="search" placeholder="Search people, titles, accounts…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="toggle">
          {([
            ['all', 'All'], ['emailed', 'Has email'],
            ['no_email', 'No email'], ['suppressed', 'Suppressed'],
          ] as const).map(([k, label]) => (
            <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Err>{error}</Err>

      {result && (
        <p className="fieldhint">
          {result.found} address{result.found === 1 ? '' : 'es'} found
          {result.missing > 0 && ` · ${result.missing} Apollo had no address for `
            + '(the credit is spent either way)'}
          {result.failed.length > 0 && ` · ${result.failed.length} failed: ${result.failed.join('; ')}`}
        </p>
      )}

      {/* THE BAR ONLY EXISTS WHEN SOMETHING IS PICKED. A permanently visible
          toolbar with a disabled paid button is an invitation; this is a
          response to a decision already taken. The header carries the answer —
          the count and the price — rather than labelling a container. */}
      {picked.size > 0 && (
        <section className="panel">
          <h4>
            {picked.size} selected
            <span className="count">
              {needAddress.length === 0
                ? 'none of them need an address'
                : `${needAddress.length} need an address · ${needAddress.length} Apollo credit`
                  + `${needAddress.length === 1 ? '' : 's'}`}
            </span>
          </h4>
          <div className="actions">
            {/* Deliberately NOT the primary button. Spend is never the warm one. */}
            <button
              disabled={needAddress.length === 0 || Boolean(running)}
              onClick={() => setConfirming(true)}>
              {running ? 'Revealing…' : `Reveal ${needAddress.length} address${needAddress.length === 1 ? '' : 'es'}`}
            </button>
            <button className="mini" onClick={() => setPicked(new Set())}>Clear</button>
          </div>
          {picked.size > needAddress.length && (
            <p className="fieldhint">
              {picked.size - needAddress.length} of those already have an address, or are
              suppressed. They are skipped rather than re-bought.
            </p>
          )}
          {confirming && (
            <p className="warnbox">
              This spends <strong>{needAddress.length} Apollo credit
              {needAddress.length === 1 ? '' : 's'}</strong> — one per person, whether or not
              Apollo turns out to hold an address.{' '}
              <button className="primary" onClick={() => void reveal()}>Yes, reveal them</button>{' '}
              <button className="mini" onClick={() => setConfirming(false)}>Cancel</button>
            </p>
          )}
        </section>
      )}

      {filtered.length === 0 ? (
        <Empty
          title={rows.length === 0 ? 'No contacts yet' : 'Nothing matches that'}
          hint={rows.length === 0
            ? 'Open an account and use Find contacts, or add someone by hand.'
            : undefined}
        />
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th className="tick">
                {/* Selects only what is VISIBLE. Ticking a header box and
                    silently including rows a filter is hiding is how somebody
                    buys forty addresses meaning to buy four. */}
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  checked={filtered.length > 0 && filtered.every((c) => picked.has(c.id))}
                  onChange={(e) => setPicked(e.target.checked
                    ? new Set(filtered.map((c) => c.id))
                    : new Set())}
                />
              </th>
              <th>Name</th><th>Title</th><th>Account</th>
              <th>Email</th><th>Stage</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className={running === c.id ? 'busy' : ''}>
                <td className="tick">
                  <input
                    type="checkbox"
                    aria-label={`Select ${c.name}`}
                    checked={picked.has(c.id)}
                    disabled={Boolean(running)}
                    onChange={(e) => setPicked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(c.id); else next.delete(c.id);
                      return next;
                    })}
                  />
                </td>
                <td><Link to={`/contacts/${c.id}`}>{c.name}</Link></td>
                <td className="meta">{c.title ?? '—'}</td>
                <td>
                  {slugOf.has(c.company_id)
                    ? <Link to={`/companies/${slugOf.get(c.company_id)}`}>
                        {byCompany.get(c.company_id)}
                      </Link>
                    : <span className="meta">—</span>}
                </td>
                <td>
                  {c.email
                    ? <span className="flag flag-ok">{c.email}</span>
                    : running === c.id
                      ? <span className="flag flag-warn">revealing…</span>
                      : <span className="flag flag-warn">not revealed</span>}
                </td>
                <td className="meta">{c.lifecycle ?? 'lead'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && <CreateMenu initial="contact"
        onClose={() => { setCreating(false); void reload(); }} />}
    </>
  );
}
